import React, { useEffect, useMemo, useState } from "react";
import { gql, useMutation, useQuery } from "@apollo/client";
import { toast } from "react-hot-toast";

const DATABASE_SAFETY = gql`
  query DatabaseSafety {
    databaseSafetyOverview {
      healthy
      databaseName
      serverVersion
      sizeBytes
      tableCount
      backupStorageConfigured
      retentionDays
      largestTables {
        tableName
        sizeBytes
      }
      latestBackup {
        id
        status
        createdAt
        completedAt
      }
    }
    databaseBackups {
      id
      status
      sizeBytes
      checksum
      errorMessage
      createdByName
      startedAt
      completedAt
      expiresAt
      createdAt
    }
    avatarMigrationAnalysis {
      remainingCount
      validCount
      invalidCount
      encodedBytes
      estimatedDecodedBytes
      largestEncodedBytes
      assetStorageConfigured
    }
    avatarMigrationBatches {
      id
      status
      requestedCount
      processedCount
      migratedCount
      failedCount
      skippedCount
      createdByName
      createdAt
      completedAt
      results {
        userId
        status
        message
        storageKey
        decodedBytes
        optimizedBytes
        width
        height
      }
    }
  }
`;

const CREATE_DATABASE_BACKUP = gql`
  mutation CreateDatabaseBackup {
    createDatabaseBackup {
      id
      status
      createdAt
    }
  }
`;

const CREATE_BACKUP_DOWNLOAD = gql`
  mutation CreateDatabaseBackupDownload($backupId: ID!) {
    createDatabaseBackupDownload(backupId: $backupId) {
      url
      expiresAt
    }
  }
`;

const MIGRATE_AVATAR_BATCH = gql`
  mutation MigrateBase64AvatarBatch($count: Int!) {
    migrateBase64AvatarBatch(count: $count) {
      id
      status
      processedCount
      migratedCount
      failedCount
      skippedCount
    }
  }
`;

const formatBytes = (bytes) => {
  const value = Number(bytes || 0);
  if (value === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const unit = Math.min(
    Math.floor(Math.log(value) / Math.log(1024)),
    units.length - 1,
  );
  return `${(value / Math.pow(1024, unit)).toFixed(unit > 1 ? 2 : 0)} ${units[unit]}`;
};

const formatDate = (value) => (value ? new Date(value).toLocaleString() : "—");

const statusClasses = {
  COMPLETED: "bg-green-100 text-green-800",
  FAILED: "bg-red-100 text-red-800",
  RUNNING: "bg-blue-100 text-blue-800",
  PENDING: "bg-yellow-100 text-yellow-800",
};

export default function DatabaseSafetySection() {
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [confirmation, setConfirmation] = useState("");
  const [downloadingId, setDownloadingId] = useState(null);
  const [avatarBatchSize, setAvatarBatchSize] = useState(1);
  const [avatarConfirmation, setAvatarConfirmation] = useState("");
  const { data, loading, error, refetch, startPolling, stopPolling } = useQuery(
    DATABASE_SAFETY,
    {
      fetchPolicy: "network-only",
    },
  );
  const [createBackup, { loading: creating }] = useMutation(
    CREATE_DATABASE_BACKUP,
  );
  const [createDownload] = useMutation(CREATE_BACKUP_DOWNLOAD);
  const [migrateAvatarBatch, { loading: migratingAvatars }] =
    useMutation(MIGRATE_AVATAR_BATCH);

  const overview = data?.databaseSafetyOverview;
  const backups = data?.databaseBackups || [];
  const avatarAnalysis = data?.avatarMigrationAnalysis;
  const avatarBatches = data?.avatarMigrationBatches || [];
  const activeBackup = useMemo(
    () =>
      backups.find((backup) => ["PENDING", "RUNNING"].includes(backup.status)),
    [backups],
  );
  const activeAvatarBatch = useMemo(
    () => avatarBatches.find((batch) => batch.status === "RUNNING"),
    [avatarBatches],
  );

  useEffect(() => {
    if (activeBackup || activeAvatarBatch) startPolling(3000);
    else stopPolling();
    return () => stopPolling();
  }, [activeAvatarBatch, activeBackup, startPolling, stopPolling]);

  const handleCreate = async () => {
    if (confirmation !== "BACKUP") return;
    try {
      await createBackup();
      toast.success("Database backup started. You can safely leave this page.");
      setConfirmation("");
      setConfirmationOpen(false);
      await refetch();
    } catch (mutationError) {
      toast.error(
        mutationError.message || "Unable to start the database backup.",
      );
    }
  };

  const handleDownload = async (backupId) => {
    setDownloadingId(backupId);
    try {
      const result = await createDownload({ variables: { backupId } });
      const url = result.data?.createDatabaseBackupDownload?.url;
      if (!url) throw new Error("A download link was not returned.");
      window.location.assign(url);
    } catch (downloadError) {
      toast.error(downloadError.message || "Unable to download this backup.");
    } finally {
      setDownloadingId(null);
    }
  };

  const handleAvatarMigration = async () => {
    if (avatarConfirmation !== `MIGRATE ${avatarBatchSize}`) return;
    try {
      await migrateAvatarBatch({
        variables: { count: avatarBatchSize },
      });
      toast.success(
        "Avatar migration batch started. Progress will update below.",
      );
      setAvatarConfirmation("");
      await refetch();
    } catch (migrationError) {
      toast.error(migrationError.message || "Avatar migration batch failed.");
    }
  };

  if (loading && !data) {
    return (
      <div className="rounded-lg bg-white p-6 shadow text-gray-500">
        Loading database status…
      </div>
    );
  }
  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-5 text-red-800">
        Database safety information could not be loaded: {error.message}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-lg bg-white p-6 shadow">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="text-lg font-medium text-gray-900">
              Database Safety
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Read-only health information and encrypted manual PostgreSQL
              backups.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setConfirmationOpen(true)}
            disabled={
              creating ||
              Boolean(activeBackup) ||
              !overview?.backupStorageConfigured
            }
            className="rounded-md bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {activeBackup ? "Backup in progress…" : "Create manual backup"}
          </button>
        </div>

        {!overview?.backupStorageConfigured && (
          <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
            Backup storage is not configured. Set a private S3 bucket before
            manual backups can run.
          </div>
        )}

        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Metric
            label="Status"
            value={overview?.healthy ? "Healthy" : "Unavailable"}
            tone={overview?.healthy ? "green" : "red"}
          />
          <Metric
            label="Database size"
            value={formatBytes(overview?.sizeBytes)}
          />
          <Metric
            label="Application tables"
            value={overview?.tableCount ?? "—"}
          />
          <Metric
            label="Backup retention"
            value={`${overview?.retentionDays ?? 30} days`}
          />
        </div>

        <div className="mt-4 text-xs text-gray-500">
          Database:{" "}
          <span className="font-medium text-gray-700">
            {overview?.databaseName}
          </span>
          {" · "}PostgreSQL {overview?.serverVersion}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-lg bg-white p-6 shadow">
          <h3 className="font-medium text-gray-900">Largest tables</h3>
          <div className="mt-4 space-y-3">
            {(overview?.largestTables || []).map((table) => (
              <div
                key={table.tableName}
                className="flex items-center justify-between gap-4 text-sm"
              >
                <span
                  className="truncate font-mono text-xs text-gray-700"
                  title={table.tableName}
                >
                  {table.tableName}
                </span>
                <span className="whitespace-nowrap text-gray-500">
                  {formatBytes(table.sizeBytes)}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-lg border border-blue-200 bg-blue-50 p-6">
          <h3 className="font-medium text-blue-900">What this backup covers</h3>
          <p className="mt-2 text-sm text-blue-800">
            The backup contains PostgreSQL records and schema. Photos, videos,
            reports, and other files stored in S3 are not copied by a database
            backup and need their own storage protection policy.
          </p>
          <p className="mt-3 text-sm text-blue-800">
            Restores intentionally remain an offline administrator procedure so
            the live database cannot be overwritten from the app.
          </p>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow">
        <div className="border-b border-gray-200 px-6 py-4">
          <h3 className="font-medium text-gray-900">Backup history</h3>
        </div>
        {backups.length === 0 ? (
          <p className="p-6 text-sm text-gray-500">
            No manual backups have been created yet.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-6 py-3">Created</th>
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3">Size</th>
                  <th className="px-6 py-3">Created by</th>
                  <th className="px-6 py-3">Expires</th>
                  <th className="px-6 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {backups.map((backup) => (
                  <React.Fragment key={backup.id}>
                    <tr>
                      <td className="whitespace-nowrap px-6 py-4 text-gray-700">
                        {formatDate(backup.createdAt)}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`rounded-full px-2.5 py-1 text-xs font-medium ${statusClasses[backup.status] || "bg-gray-100 text-gray-700"}`}
                        >
                          {backup.status.toLowerCase()}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-gray-600">
                        {backup.sizeBytes ? formatBytes(backup.sizeBytes) : "—"}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-gray-600">
                        {backup.createdByName}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-gray-600">
                        {formatDate(backup.expiresAt)}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          type="button"
                          onClick={() => handleDownload(backup.id)}
                          disabled={
                            backup.status !== "COMPLETED" ||
                            downloadingId === backup.id ||
                            new Date(backup.expiresAt) <= new Date()
                          }
                          className="font-medium text-sky-600 hover:text-sky-800 disabled:cursor-not-allowed disabled:text-gray-300"
                        >
                          {downloadingId === backup.id
                            ? "Preparing…"
                            : "Download"}
                        </button>
                      </td>
                    </tr>
                    {backup.errorMessage && (
                      <tr className="bg-red-50">
                        <td
                          colSpan="6"
                          className="px-6 py-3 text-xs text-red-700"
                        >
                          {backup.errorMessage}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="rounded-lg bg-white p-6 shadow">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h3 className="font-medium text-gray-900">
              Legacy avatar migration
            </h3>
            <p className="mt-1 max-w-3xl text-sm text-gray-500">
              Move profile photos stored as base64 database values into the
              application asset bucket. Each batch runs sequentially, one image
              at a time, with a short pause between uploads.
            </p>
          </div>
          <span
            className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium ${avatarAnalysis?.remainingCount ? "bg-amber-100 text-amber-800" : "bg-green-100 text-green-800"}`}
          >
            {avatarAnalysis?.remainingCount || 0} remaining
          </span>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Metric
            label="Valid images"
            value={avatarAnalysis?.validCount ?? "—"}
          />
          <Metric
            label="Invalid images"
            value={avatarAnalysis?.invalidCount ?? "—"}
            tone={avatarAnalysis?.invalidCount ? "red" : undefined}
          />
          <Metric
            label="Encoded DB size"
            value={formatBytes(avatarAnalysis?.encodedBytes)}
          />
          <Metric
            label="Estimated images"
            value={formatBytes(avatarAnalysis?.estimatedDecodedBytes)}
          />
        </div>

        {!avatarAnalysis?.assetStorageConfigured && (
          <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            Application asset storage is not configured. Check AWS_S3_BUCKET
            before migrating.
          </div>
        )}

        {Boolean(avatarAnalysis?.validCount) && (
          <div className="mt-5 rounded-lg border border-amber-200 bg-amber-50 p-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:items-end">
              <div>
                <label
                  htmlFor="avatar-batch-size"
                  className="block text-sm font-medium text-gray-700"
                >
                  Batch size
                </label>
                <select
                  id="avatar-batch-size"
                  value={avatarBatchSize}
                  onChange={(event) => {
                    setAvatarBatchSize(Number(event.target.value));
                    setAvatarConfirmation("");
                  }}
                  className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2"
                >
                  {[1, 2, 3, 4, 5].map((count) => (
                    <option key={count} value={count}>
                      {count} avatar{count > 1 ? "s" : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  htmlFor="avatar-migration-confirmation"
                  className="block text-sm font-medium text-gray-700"
                >
                  Type{" "}
                  <span className="font-mono font-bold">
                    MIGRATE {avatarBatchSize}
                  </span>
                </label>
                <input
                  id="avatar-migration-confirmation"
                  value={avatarConfirmation}
                  onChange={(event) =>
                    setAvatarConfirmation(event.target.value)
                  }
                  className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2"
                />
              </div>
              <button
                type="button"
                onClick={handleAvatarMigration}
                disabled={
                  migratingAvatars ||
                  Boolean(activeAvatarBatch) ||
                  !avatarAnalysis?.assetStorageConfigured ||
                  avatarConfirmation !== `MIGRATE ${avatarBatchSize}`
                }
                className="rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {activeAvatarBatch || migratingAvatars
                  ? "Migrating sequentially…"
                  : `Migrate next ${avatarBatchSize}`}
              </button>
            </div>
            <p className="mt-3 text-xs text-amber-800">
              Create and verify a database backup first. This action uploads
              images and conditionally replaces only matching base64 values; it
              does not compact the database.
            </p>
          </div>
        )}

        {avatarBatches.length > 0 && (
          <div className="mt-6 overflow-x-auto">
            <h4 className="mb-3 text-sm font-medium text-gray-800">
              Recent migration batches
            </h4>
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-3 py-2">Date</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Migrated</th>
                  <th className="px-3 py-2">Failed</th>
                  <th className="px-3 py-2">Skipped</th>
                  <th className="px-3 py-2">Run by</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {avatarBatches.map((batch) => (
                  <React.Fragment key={batch.id}>
                    <tr>
                      <td className="whitespace-nowrap px-3 py-3">
                        {formatDate(batch.createdAt)}
                      </td>
                      <td className="px-3 py-3">
                        {batch.status.toLowerCase().replaceAll("_", " ")}
                      </td>
                      <td className="px-3 py-3 text-green-700">
                        {batch.migratedCount}
                      </td>
                      <td className="px-3 py-3 text-red-700">
                        {batch.failedCount}
                      </td>
                      <td className="px-3 py-3 text-amber-700">
                        {batch.skippedCount}
                      </td>
                      <td className="whitespace-nowrap px-3 py-3">
                        {batch.createdByName}
                      </td>
                    </tr>
                    {Boolean(batch.results?.length) && (
                      <tr className="bg-gray-50">
                        <td
                          colSpan="6"
                          className="px-3 py-2 text-xs text-gray-600"
                        >
                          {batch.results
                            .map(
                              (result) => {
                                const optimization = result.optimizedBytes
                                  ? ` · ${formatBytes(result.decodedBytes)} → ${formatBytes(result.optimizedBytes)} · ${result.width}×${result.height}px`
                                  : "";
                                const message = result.message
                                  ? ` · ${result.message}`
                                  : "";
                                return `User ${result.userId}: ${result.status.toLowerCase()}${optimization}${message}`;
                              },
                            )
                            .join(" · ")}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {confirmationOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="backup-confirmation-title"
        >
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h3
              id="backup-confirmation-title"
              className="text-lg font-medium text-gray-900"
            >
              Create a production database backup?
            </h3>
            <p className="mt-2 text-sm text-gray-600">
              This may temporarily increase database and network activity. Only
              one backup can run at a time.
            </p>
            <label
              className="mt-4 block text-sm font-medium text-gray-700"
              htmlFor="backup-confirmation"
            >
              Type <span className="font-mono font-bold">BACKUP</span> to
              continue
            </label>
            <input
              id="backup-confirmation"
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
              autoFocus
              className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
            />
            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setConfirmationOpen(false);
                  setConfirmation("");
                }}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreate}
                disabled={confirmation !== "BACKUP" || creating}
                className="rounded-md bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-50"
              >
                {creating ? "Starting…" : "Start backup"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, tone }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
        {label}
      </p>
      <p
        className={`mt-1 text-xl font-semibold ${tone === "green" ? "text-green-700" : tone === "red" ? "text-red-700" : "text-gray-900"}`}
      >
        {value}
      </p>
    </div>
  );
}
